import type { Dispatch, SetStateAction } from "react";
import type {
  GhlAvailPipeline,
  GhlConnection,
  GhlNewPipelineDraft,
  LocationOption,
} from "./ghlSectionShared";

export function GhlConnectionAddPipelineForm(props: {
  conn: GhlConnection;
  showAddPipe: boolean;
  newPipe: GhlNewPipelineDraft;
  setNewPipe: Dispatch<SetStateAction<GhlNewPipelineDraft>>;
  loading: boolean;
  loadingPipes: boolean;
  availPipelines: GhlAvailPipeline[];
  knownProductTypes: string[];
  locations: LocationOption[];
  autofillMsg: { text: string; ok: boolean } | null;
  onOpenAddPipeline: () => void;
  onAddPipeline: () => void;
  onCancelAddPipeline: () => void;
  tryAutofillFromName: (pipelineName: string) => void;
}) {
  const {
    conn,
    showAddPipe,
    newPipe,
    setNewPipe,
    loading,
    loadingPipes,
    availPipelines,
    knownProductTypes,
    locations,
    autofillMsg,
    onOpenAddPipeline,
    onAddPipeline,
    onCancelAddPipeline,
    tryAutofillFromName,
  } = props;

  if (showAddPipe) {
    return (
      <div className="px-4 py-3 bg-gray-50 space-y-2">
        <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Add pipeline</div>
        {loadingPipes ? (
          <div className="text-xs text-gray-400">Loading pipelines from GHL…</div>
        ) : (
          <select
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
            value={newPipe.ghl_pipeline_id}
            onChange={(e) => {
              const selected = availPipelines.find((p) => p.id === e.target.value);
              setNewPipe((p) => ({
                ...p,
                ghl_pipeline_id: e.target.value,
                pipeline_name: selected?.name ?? p.pipeline_name,
              }));
              if (selected) tryAutofillFromName(selected.name);
            }}
          >
            <option value="">Select GHL pipeline…</option>
            {availPipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        {autofillMsg && (
          <div
            className={`text-xs px-2 py-1 rounded-md ${
              autofillMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
            }`}
          >
            {autofillMsg.text}
          </div>
        )}
        <div>
          <input
            list={`ghl-product-types-${conn.id}`}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            placeholder="Product type (e.g. sheds, steel structures)"
            value={newPipe.product_type}
            onChange={(e) => setNewPipe((p) => ({ ...p, product_type: e.target.value }))}
          />
          <datalist id={`ghl-product-types-${conn.id}`}>
            {knownProductTypes.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
        <select
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
          value={newPipe.location_id}
          onChange={(e) => setNewPipe((p) => ({ ...p, location_id: e.target.value }))}
        >
          <option value="">Org-wide (no specific location)</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void onAddPipeline()}
            disabled={loading}
            className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            Add pipeline
          </button>
          <button
            type="button"
            onClick={onCancelAddPipeline}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void onOpenAddPipeline()}
      className="w-full text-left px-4 py-2.5 text-xs text-indigo-600 hover:bg-indigo-50 font-medium"
    >
      + Add pipeline
    </button>
  );
}
